/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { CreditCardField } from '@/payment-services/components/CreditCardForm/CreditCardField';
import '@/payment-services/components/CreditCardForm/CreditCardForm.css';
import {
  Amex,
  Diners,
  Discover,
  Maestro,
  Mastercard,
  Visa,
} from '@/payment-services/icons';
import { CardType } from '@adobe-commerce/payment-services-sdk/payment';
import { Icon, Skeleton, SkeletonRow } from '@adobe-commerce/elsie/components';
import { useText } from '@adobe-commerce/elsie/i18n';
import { classes } from '@adobe-commerce/elsie/lib';
import { FunctionComponent } from 'preact';
import { HTMLAttributes } from 'preact/compat';

export interface CreditCardFormProps extends HTMLAttributes<HTMLDivElement> {
  cardContainerId: string;
  cardNumberContainerId: string;
  expirationDateContainerId: string;
  securityCodeContainerId: string;
  eligibleCardTypes: CardType[];
  selectedCardType: CardType | null;
  validationErrors: { [key: string]: string };
  isLoading?: boolean;
}

export const CreditCardForm: FunctionComponent<CreditCardFormProps> = ({
  className,
  cardContainerId,
  cardNumberContainerId,
  expirationDateContainerId,
  securityCodeContainerId,
  eligibleCardTypes,
  selectedCardType,
  validationErrors,
  isLoading = true,
  ...props
}) => {
  const labels = useText({
    cardNumber: 'PaymentServices.CreditCard.formFields.number.label',
    expirationDate:
      'PaymentServices.CreditCard.formFields.expirationDate.label',
    cvv: 'PaymentServices.CreditCard.formFields.cvv.label',
  });

  return (
    <>
      <Skeleton
        data-testid="loading-indicator"
        className={classes([
          'payment-services-credit-card-form__loading',
          isLoading ? '' : 'hidden',
        ])}
      >
        <SkeletonRow
          variant="row"
          lines={1}
          fullWidth={true}
          size="xsmall"
          multilineGap="xsmall"
        />
        <SkeletonRow variant="row" size="xsmall" fullWidth={false} />
        <SkeletonRow variant="row" size="xsmall" fullWidth={false} />
        <SkeletonRow variant="row" size="xsmall" fullWidth={false} />
      </Skeleton>

      <div
        id={cardContainerId}
        data-testid={cardContainerId}
        {...props}
        className={classes([
          'payment-services-credit-card-form',
          className,
          isLoading ? 'hidden' : '',
        ])}
      >
        <div
          id="eligible-cards"
          data-testid="eligible-cards"
          className={classes(['payment-services-credit-card-form__eligible-cards'])}
        >
          {eligibleCardTypes.map((card) => {
            let iconSource;
            switch (card.code) {
              case 'amex':
                iconSource = Amex;
                break;
              case 'mastercard':
                iconSource = Mastercard;
                break;
              case 'maestro':
                iconSource = Maestro;
                break;
              case 'discover':
                iconSource = Discover;
                break;
              case 'visa':
                iconSource = Visa;
                break;
              case 'diners':
                iconSource = Diners;
                break;
            }

            return (
              <Icon
                key={card.type}
                id={`payment-services-credit-card-form__eligible-cards-${card.code}`}
                data-testid={`payment-services-credit-card-form__eligible-cards-${card.code}`}
                source={iconSource}
                viewBox="0 0 37 24"
                className={classes([
                  'payment-services-credit-card-form__eligible-cards-icon',
                  !selectedCardType || selectedCardType.code !== card.code
                    ? 'payment-services-credit-card-form__eligible-cards-unselected'
                    : 'payment-services-credit-card-form__eligible-cards-selected',
                ])}
                title={card.type}
              />
            );
          })}
        </div>

        <CreditCardField
          className="payment-services-credit-card-form__card-number"
          containerId={cardNumberContainerId}
          error={validationErrors.number}
          label={labels.cardNumber}
        />

        <CreditCardField
          className="payment-services-credit-card-form__expiration-date"
          containerId={expirationDateContainerId}
          error={validationErrors.expirationDate}
          label={labels.expirationDate}
        />

        <CreditCardField
          className="payment-services-credit-card-form__security-code"
          containerId={securityCodeContainerId}
          error={validationErrors.cvv}
          label={labels.cvv}
        />
      </div>
    </>
  );
};
